import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import I18n from 'utils/I18n';
import Cldr from 'utils/Cldr';
import { trackV2 } from 'utils/analytics';

import CreditCardForm from 'components/CreditCardForm';

import {
  postAdyenProductCheckout,
  postAdyenTrialCheckout
} from '../../actions';

import styles from './index.scss';
import visa from './assets/visa.svg';
import mc from './assets/mc.svg';
import discover from './assets/discover.svg';
import amex from './assets/amex.svg';
import paypal from './assets/paypal.svg';

import CheckoutError from './CheckoutError';
import PayPalCheckout from './PayPalCheckout';
import { analyticsDataProps } from '../../shared/prop-types';

const PAYMENT_METHOD_CREDIT = 'credit';
const PAYMENT_METHOD_PAYPAL = 'paypal';

export const I18nPrefix = 'strava.subscriptions.checkout.form';

export class Form extends React.Component {
  state = {
    paymentMethod: PAYMENT_METHOD_CREDIT
  };

  handleOnChange = (method) => {
    const { paymentMethod } = this.state;
    if (paymentMethod !== method) {
      this.setState({ paymentMethod: method });
    }
  };

  ctaText = () => {
    const { isTrial, fullPromo, billedToday, currency } = this.props;

    if (isTrial) {
      return I18n.t(`${I18nPrefix}.submit_cta.start_trial`);
    }
    if (fullPromo) {
      return I18n.t(`${I18nPrefix}.submit_cta.start_membership`);
    }

    return I18n.t(`${I18nPrefix}.submit_cta.securely_pay`, {
      price: Cldr.formatCurrency(billedToday, currency)
    });
  };

  handleCreditCardFormSubmit = (encryptCardData) => {
    const {
      sku,
      promotionCode,
      isTrial,
      analyticsData,
      handlePostAdyenProductCheckout,
      handlePostAdyenTrialCheckout
    } = this.props;

    trackV2({
      category: 'summit_upsell',
      page: 'cart',
      action: 'finish_payment',
      properties: {
        ...analyticsData,
        subscription_type: isTrial ? 'trial' : 'organic'
      }
    });

    const formData = {
      adyen_card_data: encryptCardData,
      sku,
      promotion: promotionCode
    };

    if (isTrial) {
      handlePostAdyenTrialCheckout(formData);
    } else {
      handlePostAdyenProductCheckout(formData);
    }
  };

  renderPayment = () => {
    const { paymentMethod } = this.state;
    const {
      adyenClientPubkey,
      checkoutSubmitting,
      promoCodeSubmitting,
      fullPromo,
      promotionCode,
      sku
    } = this.props;

    if (this.props.checkoutError) {
      return <CheckoutError />;
    }

    if (paymentMethod === PAYMENT_METHOD_CREDIT) {
      return (
        <CreditCardForm
          adyenClientPubkey={adyenClientPubkey}
          submitHandler={this.handleCreditCardFormSubmit}
          isSubmitting={checkoutSubmitting}
          disabledAll={checkoutSubmitting || promoCodeSubmitting}
          ctaText={this.ctaText()}
          showRequiredCopy={!fullPromo}
          analyticsData={this.props.analyticsData}
          isTrial={this.props.isTrial}
          isStudentPromotion={this.props.isStudentPromotion}
        />
      );
    }

    return <PayPalCheckout sku={sku} promotionCode={promotionCode} />;
  };

  render() {
    const { paymentMethod } = this.state;
    const {
      showPaypal,
      checkoutSubmitting,
      promoCodeSubmitting,
      checkoutSuccess,
      checkoutSuccessRedirectPath
    } = this.props;

    if (checkoutSuccess) {
      window.location = checkoutSuccessRedirectPath;
    }

    return (
      <div className={styles.checkoutForm}>
        <div className={`${styles.card} card`}>
          <div className={styles.paymentMethods}>
            <div className={styles.paymentTitle}>
              {I18n.t(`${I18nPrefix}.payment_method`)}
            </div>
            <div
              data-test="payment-options"
              className={styles.paymentOptionList}
            >
              <label
                data-test="payment-option-credit"
                htmlFor="credit"
                className={styles.paymentOption}
              >
                <input
                  id="credit"
                  type="radio"
                  name="payment-method"
                  className={styles.input}
                  checked={paymentMethod === PAYMENT_METHOD_CREDIT}
                  onChange={() => this.handleOnChange(PAYMENT_METHOD_CREDIT)}
                  disabled={checkoutSubmitting || promoCodeSubmitting}
                />
                {I18n.t(`${I18nPrefix}.credit_or_debit`)}
                <div className={styles.cardsContainer}>
                  <img className={styles.cardImg} src={visa} alt="visa" />
                  <img className={styles.cardImg} src={mc} alt="master card" />
                  <img
                    className={styles.cardImg}
                    src={discover}
                    alt="discover"
                  />
                  <img
                    className={styles.cardImg}
                    src={amex}
                    alt="american express"
                  />
                </div>
              </label>
              {showPaypal && (
                <label
                  data-test="payment-option-paypal"
                  htmlFor="paypal"
                  className={styles.paymentOption}
                >
                  <input
                    id="paypal"
                    type="radio"
                    name="payment-method"
                    className={styles.input}
                    checked={paymentMethod === PAYMENT_METHOD_PAYPAL}
                    onChange={() => this.handleOnChange(PAYMENT_METHOD_PAYPAL)}
                    disabled={checkoutSubmitting || promoCodeSubmitting}
                  />
                  <img className={styles.paypalImg} src={paypal} alt="paypal" />
                </label>
              )}
            </div>
          </div>

          <div className={styles.divider} />

          <div className={styles.payment}>{this.renderPayment()}</div>
        </div>
      </div>
    );
  }
}

Form.propTypes = {
  handlePostAdyenProductCheckout: PropTypes.func.isRequired,
  handlePostAdyenTrialCheckout: PropTypes.func.isRequired,
  analyticsData: analyticsDataProps.isRequired,
  adyenClientPubkey: PropTypes.string.isRequired,
  sku: PropTypes.string.isRequired,
  checkoutError: PropTypes.bool,
  checkoutSubmitting: PropTypes.bool,
  promoCodeSubmitting: PropTypes.bool,
  isTrial: PropTypes.bool,
  showPaypal: PropTypes.bool,
  fullPromo: PropTypes.bool,
  promotionCode: PropTypes.string,
  billedToday: PropTypes.number,
  currency: PropTypes.string.isRequired,
  checkoutSuccess: PropTypes.bool.isRequired,
  checkoutSuccessRedirectPath: PropTypes.string.isRequired,
  isStudentPromotion: PropTypes.bool
};

Form.defaultProps = {
  checkoutError: false,
  checkoutSubmitting: false,
  promoCodeSubmitting: false,
  isTrial: false,
  showPaypal: false,
  fullPromo: false,
  promotionCode: '',
  billedToday: 0,
  isStudentPromotion: false
};

const mapStateToProps = (state) => ({
  adyenClientPubkey: state.adyen_client_pubkey,
  analyticsData: state.analytics_data,
  checkoutError: state.checkout_error,
  checkoutSubmitting: state.checkout_submitting,
  promoCodeSubmitting: state.promo_code_submitting,
  sku: state.products[state.duration].sku,
  checkoutSuccess: state.checkout_success,
  checkoutSuccessRedirectPath: state.checkout_success_redirect_path,
  isTrial: state.isTrial,
  showPaypal: state.showPaypal,
  fullPromo: state.products.promotion.full,
  promotionCode: state.products.promotion.code,
  billedToday: state.products[state.duration].billedToday,
  currency: state.products[state.duration].currency,
  isStudentPromotion: state.isStudentPromotion
});

export default connect(
  mapStateToProps,
  {
    handlePostAdyenProductCheckout: postAdyenProductCheckout,
    handlePostAdyenTrialCheckout: postAdyenTrialCheckout
  }
)(Form);
